import React, { useMemo } from "react";
import * as api from "../services/api";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { useLoaderData, useRevalidator } from "react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faEye,
  faFileCode,
  faPenToSquare,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { minutesToDuration } from "../utils/formatter";
import { formData, FormErrorList, useFormAction } from "../form";
import ButtonLoader from "../components/ButtonLoader";
import {
  compose,
  groupBy,
  indexBy,
  updateInIfExists,
} from "shared/src/util.mjs";
import { currentLocationFromRequest } from "../components/LocationSelector";
import AutocompleteSelect from "../components/AutocompleteSelect";
import { ServiceTemplateDialog } from "../components/services/ServiceTemplateDialog";
import { JsonForms } from "@jsonforms/react";
import { materialRenderers } from "../components/jsonForms/materialRenderers.jsx";
import { materialCells } from "@jsonforms/material-renderers";

export async function loader({ params, request }) {
  const res = await api.loadserviceEditData(
    {
      id: params.id,
      location_id: currentLocationFromRequest(request),
      calendarFilter: { visible: { eq: true } },
    },
    null,
    api.contextFromRequest(request),
  );
  if (res.data.service == null) {
    throw new Response("Not Found", { status: 404, statusText: "Not Found" });
  }

  return res;
}

function Info({ label, value }) {
  return (
    <Box display="flex" alignItems="center" gap="0.5rem">
      <strong>{label}:</strong> {value}
    </Box>
  );
}

function BooleanValue({ value }) {
  return value ? (
    <FontAwesomeIcon icon={faCheck} />
  ) : (
    <FontAwesomeIcon icon={faXmark} />
  );
}

function ActualCalendarValue({ locationService, calendarById }) {
  const actual_calendar = locationService?.actual_calendar ?? {};

  return [
    ...(actual_calendar.types ?? []),
    ...(actual_calendar.ids ?? [])
      .map((id) => calendarById[id]?.name)
      .filter((n) => n != null),
  ].join(", ");
}

function BasicInfoDialog({ setEditService, editService }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.patchService(formData(e.currentTarget)),
    () => {
      revalidator.revalidate();
      setEditService(null);
    },
  );

  return (
    <Dialog open={true} onClose={() => setEditService(null)}>
      <DialogTitle>{t("service.editTitle")}</DialogTitle>
      <form onSubmit={onSubmit}>
        <input type="hidden" name="id" value={editService.id} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <TextField
              name="name"
              label={t("services.name")}
              defaultValue={editService.name}
              required
            />
            <TextField
              name="color"
              label={t("services.color")}
              defaultValue={editService.color}
              type="color"
              required
            />
            <FormControlLabel
              name="create_reservation"
              control={
                <Checkbox defaultChecked={editService.create_reservation} />
              }
              label={"Vytvoření rezervace"}
            />
            <FormControlLabel
              name="change_reservation_to"
              control={
                <Checkbox defaultChecked={editService.change_reservation_to} />
              }
              label={"Změna rezervace na tuto službu"}
            />
            <FormControlLabel
              name="requires_braces"
              control={
                <Checkbox defaultChecked={editService.requires_braces} />
              }
              label={"Vyžaduje rovnátka"}
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditService(null)}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function BasicInfo({ service }) {
  const { t } = useTranslation();
  const [editService, setEditService] = React.useState();
  const [editServiceTemplate, setEditServiceTemplate] = React.useState(null);
  const [previewServiceTemplate, setPreviewServiceTemplate] =
    React.useState(null);

  return (
    <>
      <Typography variant="h1" sx={{ mb: 2 }}>
        {service.name}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Box>
              <Info label={t("services.name")} value={service.name} />
              <Info
                label={t("services.color")}
                value={
                  <Box
                    sx={{
                      backgroundColor: service.color,
                      width: "1rem",
                      height: "1rem",
                    }}
                  ></Box>
                }
              />
              <Info
                label="Vytvoření rezervace"
                value={<BooleanValue value={service.create_reservation} />}
              />
              <Info
                label="Změna rezervace na tuto službu"
                value={<BooleanValue value={service.change_reservation_to} />}
              />
              <Info
                label="Vyžaduje rovnátka"
                value={<BooleanValue value={service.requires_braces} />}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setEditService(service)}
                startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
                size="small"
              >
                Upravit
              </Button>
              <Button
                variant="outlined"
                onClick={() => setEditServiceTemplate(service)}
                startIcon={<FontAwesomeIcon icon={faFileCode} />}
              >
                Upravit šablonu
              </Button>
              <Button
                variant="outlined"
                onClick={() => setPreviewServiceTemplate(service)}
                startIcon={<FontAwesomeIcon icon={faEye} />}
              >
                Zobrazit náhled šablony
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {editService && (
        <BasicInfoDialog
          editService={editService}
          setEditService={setEditService}
        />
      )}
      {editServiceTemplate && (
        <ServiceTemplateDialog
          editServiceTemplate={editServiceTemplate}
          setEditServiceTemplate={setEditServiceTemplate}
        />
      )}
      {previewServiceTemplate && (
        <PreviewServiceTemplateDialog
          previewServiceTemplate={previewServiceTemplate}
          setPreviewServiceTemplate={setPreviewServiceTemplate}
        />
      )}
    </>
  );
}

const locationInfoFormConverter = compose((formData) =>
  updateInIfExists(formData, ["locationServices"], (v) => Object.values(v)),
);

function LocationInfoDialog({ editService, setEditService, locations }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) =>
      api.patchService(locationInfoFormConverter(formData(e.currentTarget))),
    () => {
      revalidator.revalidate();
      setEditService(null);
    },
  );

  const locationServiceByLocationId = indexBy(
    editService.locationServices,
    (sl) => sl.location.id,
  );
  const [locationIds, setLocationIds] = React.useState(
    new Set(Object.keys(locationServiceByLocationId)),
  );

  return (
    <Dialog open={true} onClose={() => setEditService(null)}>
      <DialogTitle>{t("service.editTitle")}</DialogTitle>
      <form onSubmit={onSubmit}>
        <input type="hidden" name="id" value={editService.id} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            {Array.from(locationIds).map((id) => {
              const currentLs = locationServiceByLocationId[id];
              const currentLocation = currentLs?.location;
              const locationOption = currentLocation
                ? { id: currentLocation.id, name: currentLocation.name }
                : null;

              return (
                <React.Fragment key={id}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <AutocompleteSelect
                        name={`locationServices.${id}.location_id`}
                        required
                        options={locations}
                        label="Pobočka"
                        defaultValue={locationOption}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <TextField
                          name={`locationServices.${id}.duration`}
                          label={t("services.duration")}
                          defaultValue={currentLs?.duration ?? ""}
                          type="number"
                          required
                        />
                        <TextField
                          name={`locationServices.${id}.real_duration`}
                          label="Reálné trvání"
                          defaultValue={currentLs?.real_duration ?? ""}
                          type="number"
                          required
                        />
                      </Box>
                      <List>
                        <ListItem
                          disablePadding
                          sx={
                            editService.type === "consultation"
                              ? { display: "none" }
                              : {}
                          }
                        >
                          <FormControlLabel
                            name={`locationServices.${id}.requires_doctor`}
                            control={
                              <Checkbox
                                defaultChecked={
                                  currentLs?.requires_doctor ?? false
                                }
                              />
                            }
                            label="Vyžaduje doktora"
                          />
                        </ListItem>
                        <ListItem disablePadding>
                          <FormControlLabel
                            name={`locationServices.${id}.requires_scan`}
                            control={
                              <Checkbox
                                defaultChecked={
                                  currentLs?.requires_scan ?? false
                                }
                              />
                            }
                            label="Vyžaduje scan"
                          />
                        </ListItem>
                        <ListItem disablePadding>
                          <FormControlLabel
                            name={`locationServices.${id}.requires_rentgen`}
                            control={
                              <Checkbox
                                defaultChecked={
                                  currentLs?.requires_rentgen ?? false
                                }
                              />
                            }
                            label="Vyžaduje rentgen"
                          />
                        </ListItem>
                      </List>
                    </Box>
                    <Box sx={{ justifySelf: "flex-end" }}>
                      <IconButton
                        onClick={() => {
                          setLocationIds((l) => {
                            const s = new Set(l);
                            s.delete(id);

                            return s;
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                </React.Fragment>
              );
            })}
            <Button
              variant="outlined"
              onClick={() => {
                setLocationIds((l) => new Set(l).add(crypto.randomUUID()));
              }}
            >
              Přidat pobočku
            </Button>
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditService(null)}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function LocationServiceInfo({ locationService, calendars }) {
  const { t } = useTranslation();
  const calendarById = indexBy(calendars, (c) => c.id);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h3">{locationService.location.name}</Typography>
      <Info
        label={t("services.duration")}
        value={minutesToDuration(locationService.duration)}
      />
      <Info
        label="Reálné trvání"
        value={minutesToDuration(locationService.real_duration)}
      />
      <Info
        label="Vyžaduje doktora"
        value={<BooleanValue value={locationService.requires_doctor} />}
      />
      <Info
        label="Vyžaduje scan"
        value={<BooleanValue value={locationService.requires_scan} />}
      />
      <Info
        label="Vyžaduje rentgen"
        value={<BooleanValue value={locationService.requires_rentgen} />}
      />
      <Info
        label="Přiřazení na"
        value={
          <ActualCalendarValue
            locationService={locationService}
            calendarById={calendarById}
          />
        }
      />
    </Box>
  );
}

function LocationInfo({ service, locations, calendarsByLocation }) {
  const [editService, setEditService] = React.useState();

  return (
    <>
      <Typography variant="h2" sx={{ mb: 2 }}>
        Pobočky
      </Typography>
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Box>
              {service.locationServices.map((ls) => (
                <LocationServiceInfo
                  key={ls.location.id}
                  locationService={ls}
                  calendars={calendarsByLocation[ls.location.id] ?? []}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex" }}>
              <Button
                onClick={() => setEditService(service)}
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
              >
                Upravit
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {editService && (
        <LocationInfoDialog
          locations={locations}
          editService={editService}
          setEditService={setEditService}
        />
      )}
    </>
  );
}

function PreviewServiceTemplateDialog({
  setPreviewServiceTemplate,
  previewServiceTemplate,
}) {
  const { t, i18n } = useTranslation();

  const template = previewServiceTemplate.templates[0].template;
  const translation = useMemo(
    () => (key, defaultMessage) => {
      return i18n.exists(`serviceTemplates:${key}`)
        ? t(`serviceTemplates:${key}`)
        : defaultMessage;
    },
    [i18n.language],
  );
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [formState, setFormState] = React.useState({});
  const handleFormChange = (data, errors) => {
    setIsFormValid(errors.length <= 0);
    if (errors) {
      console.log(errors);
    }
    setFormState(data);
  };
  const [formSubmitted, setFormSubmitted] = React.useState(false);

  return (
    <Dialog
      open={true}
      onClose={() => setPreviewServiceTemplate(null)}
      slotProps={{ paper: { sx: { minWidth: 1200 } } }}
    >
      <DialogTitle>{t("service.templatePreviewTitle")}</DialogTitle>

      <DialogContent>
        {template && template.schema ? (
          <JsonForms
            schema={JSON.parse(template.schema)}
            uischema={JSON.parse(template.uischema)}
            data={formState}
            renderers={materialRenderers}
            cells={materialCells}
            onChange={({ data, errors }) => handleFormChange(data, errors)}
            config={{ forceShow: formSubmitted }}
            i18n={{ translate: translation }}
          />
        ) : (
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("client.noTemplateAvailable")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setPreviewServiceTemplate(null)}
        >
          {t("form.cancel")}
        </Button>

        <Typography
          sx={{
            color: isFormValid ? "green" : "_components.alert.error.color",
          }}
        >
          {`Formulář dle schématu validní:`}{" "}
          <BooleanValue value={isFormValid}></BooleanValue>
        </Typography>

        <Button
          variant="contained"
          onClick={() => setFormSubmitted(true)}
          type="submit"
        >
          {t("form.validate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ServiceEditPage() {
  const loaderData = useLoaderData();
  const service = loaderData.data.service;
  const locations = loaderData.data.company?.locations ?? [];
  const calendarsByLocation = React.useMemo(
    () => groupBy(loaderData.data.calendars?.data ?? [], (c) => c.location_id),
    [loaderData],
  );

  return (
    <>
      <BasicInfo service={service} />
      <LocationInfo
        service={service}
        locations={locations}
        calendarsByLocation={calendarsByLocation}
      />
    </>
  );
}
